import React, {
    useCallback,
    useContext,
    useEffect,
    useState
} from "react";
import axios from "axios";
import {
    Alert,
    Button,
    OverlayTrigger,
    Table,
    Tooltip
} from "react-bootstrap";
import PropTypes from "prop-types";

import ShopsContext from "../../../context/ShopsContext";
import Loading from "../../../components/Loading";
import PriceFormatter from "../../../components/priceFormatter";
import AddSegmentModal from "./modal/AddSegmentModal";
import EditSegmentModal from "./modal/EditSegmentModal";
import DeleteSegmentModal from "./modal/DeleteSegmentModal";

function formatDuration(minutes) {
    const hours = Math.floor(minutes / 60);
    const remaining = minutes % 60;
    if(hours > 0 && remaining > 0) {
        return hours + " uur " + remaining + " min";
    }
    if(hours > 0) {
        return hours + " uur";
    }
    return minutes + " min";
}

function formatStartTime(startTime) {
    return startTime.substring(0, 5);
}

function ProductDetailSegments({ product }) {
    const shopsContext = useContext(ShopsContext);
    const [segments, setSegments] = useState(null);
    const [error, setError] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [segmentToEdit, setSegmentToEdit] = useState(null);
    const [segmentToDelete, setSegmentToDelete] = useState(null);

    const getSegments = useCallback(async () => {
        if(!product) return;
        setSegments(null);
        setError(null);
        try {
            const response = await axios.post("/getRentalProductSegments", {
                shopId: shopsContext.currentShop.id,
                productId: product.id,
            });
            if(response.data.valid) {
                setSegments(response.data.segments);
            } else {
                setError("Er ging iets fout. Probeer het later opnieuw. (" + response.data.error + ")");
            }
        } catch(requestError) {
            console.error(requestError);
            setError("Er ging iets fout. Probeer het later opnieuw.");
        }
    }, [shopsContext.currentShop.id, product]);

    useEffect(() => {
        getSegments();
    }, [getSegments]);

    const handleToggleEnabled = useCallback(async (segmentId, enabled) => {
        setError(null);
        setSegments(prev => prev ? prev.map(item =>
            item.id === segmentId ? { ...item, enabled } : item
        ) : prev);
        try {
            const response = await axios.post("/setRentalProductSegmentEnabled", {
                shopId: shopsContext.currentShop.id,
                productId: product.id,
                segmentId,
                enabled: enabled ? 1 : 0,
            });
            if(!response.data.valid) {
                setSegments(prev => prev ? prev.map(item =>
                    item.id === segmentId ? { ...item, enabled: !enabled } : item
                ) : prev);
                setError("Er ging iets fout bij het wijzigen van de status. (" + response.data.error + ")");
            }
        } catch(requestError) {
            console.error(requestError);
            setSegments(prev => prev ? prev.map(item =>
                item.id === segmentId ? { ...item, enabled: !enabled } : item
            ) : prev);
            setError("Er ging iets fout bij het wijzigen van de status. Probeer het later opnieuw.");
        }
    }, [shopsContext.currentShop.id, product]);

    if(!product) {
        return <Loading/>;
    }

    return (
        <React.Fragment>
            { error && (
                <Alert variant="danger">{ error }</Alert>
            )}

            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="mb-0">Segmenten</h4>
                <OverlayTrigger overlay={
                    <Tooltip id="addSegment">Nieuw segment</Tooltip>
                }>
                    <Button
                        variant="secondary"
                        size="sm"
                        aria-label="Segment toevoegen"
                        onClick={ () => setShowAddModal(true) }
                    >
                        <i className="fas fa-fw fa-plus"/>
                    </Button>
                </OverlayTrigger>
            </div>

            <Table size="sm" className="results" hover={ segments && segments.length > 0 }>
                <thead className="thead-light">
                <tr>
                    <th className="text-left">Starttijd</th>
                    <th className="text-left">Duur</th>
                    <th className="text-left">Prijs</th>
                    <th className="text-center">Actief</th>
                    <th className="text-center">Acties</th>
                </tr>
                </thead>
                <tbody>
                { segments === null ? (
                    <tr>
                        <td colSpan={ 5 }>
                            <Loading/>
                        </td>
                    </tr>
                ) : segments.length === 0 ? (
                    <tr>
                        <td colSpan={ 5 } className="text-center">
                            <h1><i className="fas fa-clock"/></h1>
                            <h4>Geen segmenten</h4>
                            <p>Er zijn nog geen segmenten aangemaakt voor dit product.</p>
                        </td>
                    </tr>
                ) : segments.map((segment) => (
                    <tr key={ segment.id }>
                        <td className="align-middle">
                            { formatStartTime(segment.startTime) }
                        </td>
                        <td className="align-middle">
                            { formatDuration(segment.durationInMinutes) }
                        </td>
                        <td className="align-middle">
                            <PriceFormatter price={ segment.price }/>
                        </td>
                        <td className="align-middle text-center">
                            <div className="custom-control custom-switch d-inline-block">
                                <input
                                    type="checkbox"
                                    className="custom-control-input"
                                    id={ `segment-enabled-${segment.id}` }
                                    checked={ segment.enabled }
                                    onChange={ () => handleToggleEnabled(segment.id, !segment.enabled) }
                                />
                                <label className="custom-control-label" htmlFor={ `segment-enabled-${segment.id}` } aria-label="Inschakelen"/>
                            </div>
                        </td>
                        <td className="align-middle text-center">
                            <Button
                                variant="outline-primary"
                                size="sm"
                                className="mr-1"
                                aria-label="Segment bewerken"
                                onClick={ () => setSegmentToEdit(segment) }
                            >
                                <i className="fas fa-pencil-alt"/>
                            </Button>
                            <Button
                                variant="outline-danger"
                                size="sm"
                                aria-label="Segment verwijderen"
                                onClick={ () => setSegmentToDelete(segment) }
                            >
                                <i className="fas fa-trash"/>
                            </Button>
                        </td>
                    </tr>
                ))}
                </tbody>
            </Table>

            <AddSegmentModal
                show={ showAddModal }
                handleClose={ () => setShowAddModal(false) }
                product={ product }
                onAdded={ getSegments }
            />

            <EditSegmentModal
                show={ segmentToEdit !== null }
                handleClose={ () => setSegmentToEdit(null) }
                product={ product }
                segment={ segmentToEdit }
                onSaved={ getSegments }
            />

            <DeleteSegmentModal
                show={ segmentToDelete !== null }
                handleClose={ () => setSegmentToDelete(null) }
                product={ product }
                segment={ segmentToDelete }
                onDeleted={ getSegments }
            />
        </React.Fragment>
    );
}
ProductDetailSegments.propTypes = {
    product: PropTypes.object,
}

export default React.memo(ProductDetailSegments);
